/**
 * The pointer's own two answers, as decoration: the card that names the aircraft under it, and the
 * chip that names the airport.
 *
 * The cards are written imperatively — a card follows an aircraft that moves every frame, and a
 * React state write per pointer event would re-render the whole scene for a decoration — so they
 * are created here, once, into the layer the page renders for them, and the script writes nothing
 * but `transform` and `data-visible`: a card that follows a moving glyph must not touch layout.
 *
 * Three deliberate choices, all of them about staying out of the page's way:
 *
 * - A card sits below its own glyph and is pushed further down until it clears every city name, and
 *   it is never drawn above the glyph it belongs to. The names are canvas text with no DOM box, so
 *   each marker's own band stands in for them: the marker is the city point (`anchor: "left"`), the
 *   name is left-anchored beside it and a line 13–27 px tall, so the band is the strip to the right
 *   of the marker. A card the globe has no room for is hidden instead — never onto a name.
 * - The pointer is read on the window, not on the map: the globe is deliberately inert
 *   (`pointer-events: none` on the stage, `interactive: false` on the map) so it can never swallow
 *   the page's wheel, drag or text selection, and a decoration that ate the pointer would be worse
 *   than one nobody can hover. The stage's own rows are the real targets, so the reading has to sit
 *   above every one of them. Mouse only: a card under a thumb would cover the text its own rule
 *   exists to protect, and a tap has no hover to leave.
 * - The ink is not this module's. An airport's glyph takes the accent while the pointer is on it,
 *   and the row's own emphasis goes when the pointer leaves — both belong to whoever owns the
 *   layers, so they arrive as callbacks and the module never touches a map.
 *
 * The layer the page renders for the cards is `aria-hidden`: this is the decoration explaining the
 * decoration, and the payload's own city list is what a reader navigates by.
 */

import type { ScreenPoint } from "@/components/atlas-declutter";
import type { FlightHit } from "@/components/atlas-planes";

/**
 * The hover card's own geometry. A card sits below its glyph and is pushed further down until it
 * clears every city name: the names are canvas text with no DOM box, so each marker's own band
 * stands in for them — the marker is the city point (`anchor: "left"`), the name is left-anchored
 * beside it and a line 13–27 px tall, so the band is the strip to the right of the marker,
 * generously sized. Downward only, which is what keeps a card out of the name row above its glyph
 * and away from the city the glyph belongs to.
 */
const NAME_BAND = { up: 18, right: 220, down: 14 };
/** How far below its own glyph a card sits: clear of the glyph, close enough to read as its own. */
const PLANE_CARD_OFFSET = 10;
const PORT_CARD_OFFSET = 8;
/** The gap a card keeps from the globe's own edge, and how many placements it may try. */
const CARD_EDGE = 4;
const CARD_PASSES = 4;

/** The one text line each card carries. `home.css` owns what these look like. */
const CARD_ID = ".atlas-card-id";
/** The flight card's own route line, and the seatbelt sign beside its designator. */
const CARD_ROUTE = ".atlas-card-route";
const BELT = ".atlas-belt";
/** The flight card's two groups: the route line with its designator, and the two cabin signs. */
const CARD_FACTS = ".atlas-card-facts";

/**
 * The two cabin signs from the supplied aircraft-panel reference, cut out of it as transparent
 * pixels rather than redrawn: the always-lit cigarette with its red cross, and the hands fastening
 * the striped belt with the green direction triangle between them. The stylesheet places them and
 * `placePlane` lights the belt; the images themselves carry no text.
 */
const NO_SMOKING_SIGN =
  '<span class="atlas-no-smoking" role="img" aria-label="No smoking">' +
  '<img src="/cabin-no-smoking-reference.png" alt="" aria-hidden="true" draggable="false" width="190" height="95">' +
  "</span>";
const BELT_SIGN =
  '<span class="atlas-belt" data-on="false" role="img" aria-label="Seatbelt sign off">' +
  '<img src="/cabin-seatbelt-reference.png" alt="" aria-hidden="true" draggable="false" width="270" height="128">' +
  "</span>";

/**
 * The two cards' own markup, created once into the layer the page renders for them. The class names
 * are the stylesheet's hooks, and the flight's card reads top to bottom: where it came from and
 * which flight it is, then the two signs a cabin carries. The words and the signs are each their own
 * group, which is what lets the card stack them without re-cutting its parts.
 */
const FLIGHT_CARD_MARKUP =
  '<span class="atlas-card-facts">' +
  '<span class="atlas-card-route"></span>' +
  '<span class="atlas-card-id"></span>' +
  "</span>" +
  '<span class="atlas-signs">' +
  NO_SMOKING_SIGN +
  BELT_SIGN +
  "</span>";
/** The airport's chip carries the code and nothing else: no code, no card. */
const PORT_CARD_MARKUP = '<span class="atlas-card-id"></span>';

/**
 * The two shapes a hit can take, one alias each, and the two coordinates the cards work in: a point
 * in the cards' own frame is both the pointer's position and a projected glyph's.
 */
type PlaneHit = Extract<FlightHit, { kind: "plane" }>;
type PortHit = Extract<FlightHit, { kind: "port" }>;

/**
 * Where the flight came from and where it is going, or nothing at all: half a route is not a route,
 * and an invented end to it would be a claim about a real flight. The designator beside it still
 * says which flight this is when the codes are unknown.
 */
const flightRoute = (hit: PlaneHit): string =>
  hit.fromCode === "" || hit.toCode === "" ? "" : `${hit.fromCode} → ${hit.toCode}`;

/** One name band on screen: the strip a card must keep clear of. */
type NameBand = { left: number; top: number; right: number; bottom: number };

/**
 * The part of a city marker this module reads: its own element, whose rect stands in for the
 * canvas-painted name beside it. Read per placement, never copied.
 */
export type MarkerNode = { readonly element: HTMLElement };

export interface AtlasCardsOptions {
  /**
   * The layer the two cards are created in — the page renders it, this module fills it, and the
   * page must render no children of its own there. Its box is the map canvas's own box, so a
   * projected point is a coordinate in it. Null (no layer yet) leaves every operation a no-op.
   */
  layer: HTMLElement | null;
  /** The globe's own box, read per call and never cached: a card is placed against it on the frame
   * it is shown. */
  box: () => DOMRect | null;
  /** The city markers whose names a card stays clear of, read per placement. */
  markers: () => readonly MarkerNode[];
  /** The glyph under a point, answered from the fleet's own painted set; null for none. */
  hitTest: (point: ScreenPoint) => FlightHit | null;
  /** Where a glyph sits in the cards' own frame, or null when there is no frame to place in. */
  project: (coordinates: [number, number]) => ScreenPoint | null;
  /** The hover ink's own half of the same read: an airport's glyph takes the accent while the
   * pointer is on it, written whatever the card does — the ink answers the pointer, the card needs
   * the room. */
  ink: (hit: FlightHit | null) => void;
  /** The row's own emphasis, released with the pointer. */
  release: () => void;
}

export interface AtlasCards {
  /** Re-place the card that is up on a frame the globe turned; a no-op while none is. */
  redraw: () => void;
  /** The hover goes: the ink, the card and the row's own emphasis. */
  clear: () => void;
  /** Drop the pointer listeners and the two cards. */
  dispose: () => void;
}

/**
 * The pointer's two cards, on a slow clock of their own: every frame the globe turns re-reads the
 * glyph under the pointer and re-places the card, and everything else — the pointer's own arrival,
 * its departure, the chapter leaving the cities — is an event.
 */
export const createAtlasCards = (options: AtlasCardsOptions): AtlasCards => {
  const { layer, box, markers, hitTest, project, ink, release } = options;

  const createCard = (markup: string): HTMLDivElement | null => {
    if (!layer) return null;
    const card = document.createElement("div");
    card.className = "atlas-card";
    card.dataset.visible = "false";
    card.innerHTML = markup;
    layer.append(card);
    return card;
  };
  const flightCard = createCard(FLIGHT_CARD_MARKUP);
  // The airport's chip is the other card and shares `.atlas-card`: it carries the code and nothing
  // else, so whatever the card's own box becomes has to read right around one short line too.
  const portCard = createCard(PORT_CARD_MARKUP);

  // Which card is up. One writer holds it, so a card swapped for the other kind — an airport's
  // chip for a flight's — cannot leave both up, and the per-frame re-decisions cost nothing until
  // the answer actually changes. Placement runs before the write: a card the globe has no room
  // for stays hidden, and is never shown at a position it was then refused.
  let cardsOn: "none" | "plane" | "port" = "none";
  // The glyph under the pointer, and where the pointer is. The aircraft move, so a card has to be
  // re-placed on every frame the globe turns — but only while one is up, which `redraw` asks.
  let hovered: FlightHit | null = null;
  let hoverPoint: ScreenPoint | null = null;

  const show = (next: "none" | "plane" | "port"): void => {
    if (next === cardsOn) return;
    cardsOn = next;
    if (flightCard) flightCard.dataset.visible = next === "plane" ? "true" : "false";
    if (portCard) portCard.dataset.visible = next === "port" ? "true" : "false";
  };

  /** Fill one of a card's own lines, and only when it changed: a card that follows its glyph writes
   * nothing per frame. */
  const writeCard = (card: HTMLDivElement, selector: string, text: string): void => {
    const line = card.querySelector<HTMLElement>(selector);
    if (line && line.textContent !== text) line.textContent = text;
  };

  /**
   * The bands of the city names that are actually on screen. A marker whose opacity the stylesheet
   * has taken to zero — the bars fade out with the closing chapter — is still measurable, but it is
   * not text anybody can see, and pushing a card down for it would look arbitrary.
   */
  const nameBands = (): NameBand[] => {
    const bands: NameBand[] = [];
    for (const entry of markers()) {
      if (getComputedStyle(entry.element).opacity === "0") continue;
      const rect = entry.element.getBoundingClientRect();
      bands.push({
        left: rect.left,
        top: rect.top - NAME_BAND.up,
        right: rect.left + NAME_BAND.right,
        bottom: rect.bottom + NAME_BAND.down,
      });
    }
    return bands;
  };

  /**
   * Place a card below its anchor, clear of every city name and inside the globe's own box.
   * Downward only, and by whole pushes: a card is never drawn above the glyph it belongs to, and
   * never onto a name — if it cannot be placed, it is hidden instead. Returns whether it is up.
   */
  const placeCard = (card: HTMLDivElement, anchor: ScreenPoint, offset: number): boolean => {
    const globe = box();
    if (!globe) return false;
    const bands = nameBands();
    let x = anchor.x;
    let y = anchor.y + offset;
    for (let pass = 0; pass < CARD_PASSES; pass += 1) {
      card.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      const rect = card.getBoundingClientRect();
      if (rect.bottom > globe.bottom - CARD_EDGE) return false;
      const overflow = rect.right - (globe.right - CARD_EDGE);
      if (overflow > 0) {
        x -= overflow;
        continue;
      }
      let push = 0;
      for (const band of bands) {
        if (
          rect.right <= band.left ||
          rect.left >= band.right ||
          rect.bottom <= band.top ||
          rect.top >= band.bottom
        ) {
          continue;
        }
        push = Math.max(push, band.bottom + CARD_EDGE - rect.top);
      }
      if (push === 0) return true;
      y += push;
    }
    return false;
  };

  /** The airport's own answer: its code, or nothing when the store never learned one — the ink is
   * the answer then, and an empty chip would be a rectangle with nothing in it. */
  const placePort = (hit: PortHit, anchor: ScreenPoint): void => {
    if (!portCard) return;
    if (hit.code === "") {
      show("none");
      return;
    }
    writeCard(portCard, CARD_ID, hit.code);
    show(placeCard(portCard, anchor, PORT_CARD_OFFSET) ? "port" : "none");
  };

  /** The aircraft's own answer: where it came from, which flight it is, and whether the sign is on. */
  const placePlane = (hit: PlaneHit, anchor: ScreenPoint): void => {
    if (!flightCard) return;
    const route = flightRoute(hit);
    writeCard(flightCard, CARD_ROUTE, route);
    writeCard(flightCard, CARD_ID, hit.number);
    const facts = flightCard.querySelector<HTMLElement>(CARD_FACTS);
    const noFacts = route === "" && hit.number === "";
    // An empty group takes the card's own gap even at no width, so a flight with neither a route nor
    // a designator is written out of the markup and shows the two signs alone — exactly the card it
    // showed before the groups existed. Written only when the answer changes, like the sign's state.
    if (facts && (facts.dataset.empty === "true") !== noFacts) {
      facts.dataset.empty = noFacts ? "true" : "false";
    }
    const belt = flightCard.querySelector<HTMLElement>(BELT);
    const on = hit.belt ? "true" : "false";
    // The sign's own state and the words that stand in for it move together, and only when the
    // state actually changed: a card redrawn every frame must not rewrite what it already says.
    if (belt && belt.dataset.on !== on) {
      belt.dataset.on = on;
      belt.setAttribute("aria-label", on === "true" ? "Seatbelt sign on" : "Seatbelt sign off");
    }
    show(placeCard(flightCard, anchor, PLANE_CARD_OFFSET) ? "plane" : "none");
  };

  /**
   * The pointer's own read, re-run on every frame the globe turns: the aircraft move, so its card
   * has to follow the projected glyph and can be pushed onto a name by a rotation alone. The ink is
   * written whatever the card does — the ink answers the pointer, the card needs the room — and a
   * hit with no projection means there is no frame to place a card in, so nothing is written.
   */
  const resolveHover = (): void => {
    const point = hoverPoint;
    if (!point) return;
    const hit = hitTest(point);
    const anchor = hit ? project(hit.coordinates) : null;
    if (hit && !anchor) return;
    hovered = hit;
    ink(hit);
    if (!hit || !anchor) {
      show("none");
      return;
    }
    if (hit.kind === "port") placePort(hit, anchor);
    else placePlane(hit, anchor);
  };

  const redraw = (): void => {
    if (hovered === null) return;
    resolveHover();
  };

  const clear = (): void => {
    hovered = null;
    ink(null);
    show("none");
    // The pointer is gone from the page, so the ink goes with it: the selection the ink belongs to
    // is the reader's pointer, and the chapter's own release is the other half of that rule.
    release();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse") {
      clear();
      return;
    }
    const globe = box();
    if (!globe) return;
    const point = { x: event.clientX - globe.left, y: event.clientY - globe.top };
    const inside =
      point.x >= 0 && point.y >= 0 && point.x <= globe.width && point.y <= globe.height;
    if (!inside) {
      clear();
      return;
    }
    hoverPoint = point;
    resolveHover();
  };

  const dispose = (): void => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointercancel", clear);
    document.documentElement?.removeEventListener("pointerleave", clear);
    flightCard?.remove();
    portCard?.remove();
  };

  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointercancel", clear);
  // `pointerleave` does not bubble, and the document element is the target that receives it when
  // the pointer leaves the window: without it a card would sit over the globe until the next move.
  document.documentElement?.addEventListener("pointerleave", clear);

  return { redraw, clear, dispose };
};
